import * as v from 'valibot'
import ValibotCommon from '@shared-schemas/common'
import { REPORT_STATUS_TYPES } from '@shared-types/reports'

export function getReportRecordMinimalSchema(
	params: { expect_short_uuid?: boolean; convert_short_uuids?: boolean } = {},
) {
	const { expect_short_uuid = false, convert_short_uuids = false } = params

	return v.object({
		id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		customer_id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `customer_id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		user_id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `user_id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		user_display_name: ValibotCommon.getNonEmptyStringSchema({ error_message: 'user_display_name cannot be empty' }),
		report_type: ValibotCommon.getNonEmptyStringSchema({ error_message: 'report_type cannot be empty' }),
		display_name: ValibotCommon.getNonEmptyStringSchema({ error_message: 'display_name cannot be empty' }),
		created_at: ValibotCommon.getDateOrIsoStringSchema(),
		updated_at: ValibotCommon.getDateOrIsoStringSchema(),

		// NOTE: not part of the table structure; created dynamically during query
		status: v.picklist(REPORT_STATUS_TYPES, `status must be one of: ${REPORT_STATUS_TYPES.join(', ')}`),
		progress: v.nullable(v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(100))),
		estimated_duration_seconds: v.nullable(v.pipe(v.pipe(v.number(), v.safeInteger(), v.minValue(0)))),
		// Type-specific shape; narrow with a report-type-specific schema (e.g. getLitmusReportMetadataSchema) before use
		report_metadata: v.nullable(v.record(v.string(), v.unknown())),
	})
}

export function getReportRecordFullSchema(params: { expect_short_uuid?: boolean; convert_short_uuids?: boolean } = {}) {
	const { expect_short_uuid = false, convert_short_uuids = false } = params

	const valid_parent_types = ['REGION', 'NETWORK']

	const base_schema = v.pipe(getReportRecordMinimalSchema(params))

	return v.object({
		...base_schema.entries,
		report_type_ref_id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `report_type_ref_id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		report_metadata: v.object({
			data_sources: ValibotCommon.getNonEmptyStringSchema({
				error_message: 'report_metadata.data_sources cannot be empty',
			}),
			days_of_week: v.array(
				ValibotCommon.getNonEmptyStringSchema({
					error_message: 'report_metadata.days_of_week item cannot be empty',
				}),
			),
			parent_id: ValibotCommon.getUuidSchema({
				expect_short_uuid,
				convert_to_full_uuid: convert_short_uuids,
				error_message: `report_metadata.parent_id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
			}),
			parent_type: v.pipe(
				v.string(),
				v.toUpperCase(),
				v.picklist(valid_parent_types, `parent_type must be one of: ${valid_parent_types.join(', ')}`),
			),
			zone_id: ValibotCommon.getNonEmptyStringSchema({
				error_message: 'report_metadata.zone_id cannot be empty',
			}),
		}),
		is_deleted: v.boolean(),
		is_canceled: v.boolean(),
	})
}

export function getReportProgressSchema(params: { expect_short_uuid?: boolean; convert_short_uuids?: boolean } = {}) {
	const base_schema = v.pipe(getReportRecordMinimalSchema(params))

	return v.object({
		id: base_schema.entries.id,
		report_type: base_schema.entries.report_type,
		status: base_schema.entries.status,
		progress: base_schema.entries.progress,
		estimated_duration_seconds: base_schema.entries.estimated_duration_seconds,
	})
}

export function getReportTotalsSchema() {
	return v.object({
		report_count: v.pipe(v.number(), v.integer(), v.minValue(0)),
		total_project_cost_usd: v.pipe(v.number(), v.finite()),
		total_project_savings_usd: v.pipe(v.number(), v.finite()),
	})
}
